import {
  CoverageFeeProductMatchType,
  isCoverageFeeProduct,
} from "@redotech/redo-return-coverage-common/is-coverage-fee-product";
import { StorefrontConfig } from "@redotech/redo-storefront-common/storefront-config";
import { CartProductType } from "@redotech/redo-storefront-widget-common/cart-product-types";
import { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";
import { getLocalStorageJson } from "@redotech/redo-web/utils/local-storage-wrapper";
import type { ShopifyCartItem } from "@redotech/shopify-client/ajax";

export const REVIEWS_PDP_BLOCK_TARGET_ID = "redo-reviews-pdp-block-target";

export interface RedoProductToSell {
  variantId: number | string;
  productId: number | string;
  price: number;
  rawCalculatedCoveragePrice: number;
  productType: CartProductType;
}

export const isRedoItem = (
  item: ShopifyCartItem,
  coverageProductIds: string[] | undefined,
) =>
  isCoverageFeeProduct({
    product: {
      vendor: item.vendor,
      sku: item.sku,
      title: item.title,
      product_id: item.product_id?.toString(),
    },
    redoCoverageProductIds: new Set(coverageProductIds ?? []),
    matchType: CoverageFeeProductMatchType.REDO_ONLY,
  });

export const isPackageProtection = (
  item: ShopifyCartItem,
  coverageProductIds: string[] | undefined,
) =>
  item.handle === "navidium-shipping-protection" ||
  isCoverageFeeProduct({
    product: {
      vendor: item.vendor,
      sku: item.sku,
      title: item.title,
      product_id: item.product_id?.toString(),
    },
    redoCoverageProductIds: new Set(coverageProductIds ?? []),
    matchType: CoverageFeeProductMatchType.REDO_AND_COMPETITORS,
  });

export const containsExcludedItemProperty = (
  item: ShopifyCartItem,
  excludedProperties: { name: string; value: string }[],
): boolean => {
  return excludedProperties.some((property) => {
    return Object.entries(item.properties || {}).some(([key, val]) => {
      return key === property.name && val === property.value;
    });
  });
};

export const REDO_DISABLE_TAGS = ["redo-hidden"];

export const getLocalStorageIgnoreExpiry = (key: string) => {
  return getLocalStorageJson(key);
};

export const getVariantIdFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    const variantId = urlObj.searchParams.get("variant");
    return variantId;
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
};

export function getCartShadowRoot(
  autocheck: WidgetAutocheck,
  shadowRootSelectorOverride?: string,
): ShadowRoot | undefined {
  const useShadowRoot =
    shadowRootSelectorOverride ?? autocheck.cartToggle.useShadowRootInCart;
  const shadowDomSelector =
    shadowRootSelectorOverride ?? autocheck.cartToggle.cartShadowRootSelector;
  if (!useShadowRoot || !shadowDomSelector) {
    return undefined;
  }
  return document.querySelector(shadowDomSelector)?.shadowRoot ?? undefined;
}

// Query both the document and the shadow root (if it exists) to make sure we get ALL elements that match the selector
export function queryDocumentAndShadowRoot(
  selector: string,
  autocheck: WidgetAutocheck,
): Element[] {
  if (!selector) {
    return [];
  }
  return [
    ...document.querySelectorAll(selector),
    ...(getCartShadowRoot(autocheck)?.querySelectorAll(selector) ?? []),
  ];
}

/**
 * Gets the appropriate storefront access token for the current hostname
 * If no matching hostname is found in multistorefront configs, returns the default token
 */
export function getStorefrontAccessToken(autocheck: WidgetAutocheck): string {
  // If no multistorefront configuration, just return the default token
  if (!autocheck.multistorefront || autocheck.multistorefront.length === 0) {
    return autocheck.storefrontAccessToken;
  }

  // Get current hostname
  const currentHostname = location.hostname;

  // Look for a matching hostname in the multistorefront config
  const matchingConfig = autocheck.multistorefront.find(
    (config: StorefrontConfig) => config.hostname === currentHostname,
  );

  // Return the matching token if found, otherwise the default token
  return matchingConfig
    ? matchingConfig.storefrontAccessToken
    : autocheck.storefrontAccessToken;
}

const PREORDER_BUTTON_DISABLED_ATTR = "data-redo-preorder-disabled";
const ORIGINAL_DISABLED_STATE_ATTR = "data-redo-original-disabled";

/** Narrows to elements with a native `disabled` property (e.g. <button>, not <div>). */
function supportsDisabled(el: Element): el is HTMLButtonElement {
  return "disabled" in el;
}

/**
 * Finds all preorder buttons on the page by checking for buttons with
 * text content containing "pre-order" or "preorder" (case-insensitive).
 */
export function findPreorderButtons(): HTMLElement[] {
  const buttons = document.querySelectorAll<HTMLElement>(
    'button[name="add"], button[type="submit"], button#add, div.add.pgroup-submit, .product-form__submit, button.am_rec_add_to_cart_button',
  );

  return Array.from(buttons).filter((button) => {
    const text = button.textContent?.toLowerCase().trim() ?? "";
    return text.includes("pre-order") || text.includes("preorder");
  });
}

/**
 * Disables preorder buttons during Shop on Site exchange flow.
 * Adds a visual indicator and prevents clicking.
 * Also re-enables buttons that were previously disabled but no longer have preorder text.
 */
export function disablePreorderButtons(): void {
  const preorderButtons = findPreorderButtons();
  const preorderButtonSet = new Set(preorderButtons);

  // Disable preorder buttons
  for (const button of preorderButtons) {
    if (button.hasAttribute(PREORDER_BUTTON_DISABLED_ATTR)) {
      continue;
    }

    // Preserve original disabled state before modifying
    if (!button.hasAttribute(ORIGINAL_DISABLED_STATE_ATTR)) {
      button.setAttribute(
        ORIGINAL_DISABLED_STATE_ATTR,
        String(supportsDisabled(button) ? button.disabled : false),
      );
    }

    button.setAttribute(PREORDER_BUTTON_DISABLED_ATTR, "true");
    if (supportsDisabled(button)) {
      button.disabled = true;
    }
    button.style.opacity = "0.5";
    button.style.cursor = "not-allowed";
    button.style.pointerEvents = "none";

    // Add message below the button
    const existingMessage = button.parentElement?.querySelector(
      ".redo-preorder-disabled-message",
    );
    if (!existingMessage) {
      const message = document.createElement("div");
      message.className = "redo-preorder-disabled-message";
      message.textContent = "Preorder items cannot be added during exchanges";
      message.style.cssText =
        "color: #d63031; font-size: 12px; margin-top: 8px; font-weight: 500;";
      button.parentElement?.insertBefore(message, button.nextSibling);
    }
  }

  // Re-enable buttons that were disabled but no longer have preorder text
  const disabledButtons = document.querySelectorAll<HTMLElement>(
    `[${PREORDER_BUTTON_DISABLED_ATTR}]`,
  );

  for (const button of disabledButtons) {
    if (!preorderButtonSet.has(button)) {
      // Button text changed - restore original state
      const wasOriginallyDisabled =
        button.getAttribute(ORIGINAL_DISABLED_STATE_ATTR) === "true";
      button.removeAttribute(PREORDER_BUTTON_DISABLED_ATTR);
      button.removeAttribute(ORIGINAL_DISABLED_STATE_ATTR);
      if (supportsDisabled(button)) {
        button.disabled = wasOriginallyDisabled;
      }
      button.style.opacity = "";
      button.style.cursor = "";
      button.style.pointerEvents = "";

      // Remove the message
      const message = button.parentElement?.querySelector(
        ".redo-preorder-disabled-message",
      );
      if (message) {
        message.remove();
      }
    }
  }
}

/**
 * Re-enables preorder buttons (used when exiting exchange flow).
 * Restores original disabled state if the button was disabled before.
 */
export function enablePreorderButtons(): void {
  const buttons = document.querySelectorAll<HTMLElement>(
    `[${PREORDER_BUTTON_DISABLED_ATTR}]`,
  );

  for (const button of buttons) {
    const wasOriginallyDisabled =
      button.getAttribute(ORIGINAL_DISABLED_STATE_ATTR) === "true";
    button.removeAttribute(PREORDER_BUTTON_DISABLED_ATTR);
    button.removeAttribute(ORIGINAL_DISABLED_STATE_ATTR);
    if (supportsDisabled(button)) {
      button.disabled = wasOriginallyDisabled;
    }
    button.style.opacity = "";
    button.style.cursor = "";
    button.style.pointerEvents = "";
  }

  // Remove messages
  const messages = document.querySelectorAll(".redo-preorder-disabled-message");
  for (const message of messages) {
    message.remove();
  }
}
